import React, { useEffect, useMemo, useState } from 'react';
import type { ScheduleSlotPayload, StaffScheduleResponse } from '../../types/api-responses';
import styles from './ScheduleTab.module.css';

/**
 * AB#1734 follow-up — page-level Schedule tab (replaces the in-modal
 * schedule section that shipped in AB#1718).
 *
 * Founder direction 2026-05-19:
 *
 *   > "why we not move the schedule on next tab that related to schedule
 *      already i mentioned that before, any reason for keep it in side the
 *      same edit page"
 *
 * Right answer was always the dedicated `Schedule` tab on the Manage
 * Staff page (it was sitting there as a `SOON` placeholder while we
 * built the editor inside the modal). This component is the editor at
 * its proper home.
 *
 * Responsibilities:
 * - Render a staff picker at the top (single-select, defaults to the
 *   first active staff with hours set, else the first staff)
 * - Load the picked staff's per-branch schedule from the BE
 * - Render the accordion editor (one card per enrolled branch)
 * - Detect cross-branch time-overlap conflicts and hard-block save
 * - Save via the parent's `setStaffSchedule(staffId, slots)` hook
 *
 * Out of scope:
 * - Editing staff identity / capabilities (still in the staff modal)
 * - Team-wide weekly grid view (future evolution of this tab)
 * - Vacation overrides, shift swaps (separate Epics)
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_INDEXES: ReadonlyArray<number> = [0, 1, 2, 3, 4, 5, 6];

type SlotKey = string;
interface ScheduleSlotState {
    branchId: number;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
}

const slotKey = (branchId: number, dayOfWeek: number): SlotKey => `${branchId}|${dayOfWeek}`;
const DEFAULT_START = '09:00';
const DEFAULT_END = '17:00';

function normalizeTimeForInput(t: string): string {
    if (!t) return DEFAULT_START;
    return t.length >= 5 ? t.slice(0, 5) : t;
}

// AB#1718 follow-up: BE serializes DayOfWeek as the enum NAME ("Monday")
// via the global JsonStringEnumConverter; normalize at the FE boundary so
// Map keys stay numeric.
const DAY_OF_WEEK_BY_NAME: Record<string, number> = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
};

function normalizeDayOfWeek(raw: unknown): number {
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
        const named = DAY_OF_WEEK_BY_NAME[raw];
        if (named !== undefined) return named;
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed)) return parsed;
    }
    return 0;
}

export interface ScheduleTabStaffOption {
    id: number;
    fullName: string;
    position?: string | null;
    photoUrl?: string | null;
    branchIds?: number[];
    isActive: boolean;
}

export interface ScheduleTabBranchOption {
    id: number;
    name: string;
    isPrimary?: boolean;
}

export interface ScheduleTabProps {
    staff: ScheduleTabStaffOption[];
    branches: ScheduleTabBranchOption[];
    getStaffSchedule: (staffId: number) => Promise<StaffScheduleResponse>;
    setStaffSchedule: (staffId: number, slots: ScheduleSlotPayload[]) => Promise<boolean>;
    onSavedToast?: (message: string) => void;
    onErrorToast?: (message: string) => void;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({
    staff,
    branches,
    getStaffSchedule,
    setStaffSchedule,
    onSavedToast,
    onErrorToast,
}) => {
    // Filter to active staff only — inactive staff don't get bookings, so
    // managing their schedule has no operational meaning.
    const eligibleStaff = useMemo(() => staff.filter(s => s.isActive), [staff]);

    const [selectedStaffId, setSelectedStaffId] = useState<number | null>(
        () => eligibleStaff[0]?.id ?? null,
    );
    const [schedule, setSchedule] = useState<Map<SlotKey, ScheduleSlotState>>(() => new Map());
    const [expandedBranchIds, setExpandedBranchIds] = useState<Set<number>>(() => new Set());
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [fetching, setFetching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastFetchError, setLastFetchError] = useState<string | null>(null);

    const selectedStaff = useMemo<ScheduleTabStaffOption | null>(
        () => eligibleStaff.find(s => s.id === selectedStaffId) ?? null,
        [eligibleStaff, selectedStaffId],
    );

    // If the staff list changes (e.g., parent reloaded) and the selected
    // staff is no longer eligible, fall back to the first available.
    useEffect(() => {
        if (eligibleStaff.length === 0) {
            setSelectedStaffId(null);
            return;
        }
        if (!eligibleStaff.some(s => s.id === selectedStaffId)) {
            setSelectedStaffId(eligibleStaff[0].id);
        }
    }, [eligibleStaff, selectedStaffId]);

    // Which branches does the selected staff's schedule editor render
    // cards for? Single-branch business: the lone branch. Multi-branch
    // with explicit enrollment: those branches. Multi-branch with empty
    // enrollment (implicit-all): zero cards + auto-promote prompt.
    const scheduleBranches = useMemo<ScheduleTabBranchOption[]>(() => {
        if (!selectedStaff) return [];
        if (branches.length === 1) return branches;
        const enrolled = new Set(selectedStaff.branchIds ?? []);
        return branches.filter(b => enrolled.has(b.id));
    }, [branches, selectedStaff]);

    const isMultiBranch = branches.length >= 2;
    const hasScheduleBranches = scheduleBranches.length > 0;

    // Fetch + seed the schedule whenever the picked staff changes.
    useEffect(() => {
        if (selectedStaffId == null) {
            setSchedule(new Map());
            setExpandedBranchIds(new Set());
            return;
        }
        let cancelled = false;
        setFetching(true);
        setLastFetchError(null);
        setErrors({});
        getStaffSchedule(selectedStaffId)
            .then(resp => {
                if (cancelled) return;
                // B34 — Filter orphan rows: a staff's StaffSchedules may
                // contain rows for branches they were un-enrolled from
                // (BusinessStaffBranches rows removed, but the BE doesn't
                // cascade-delete schedule rows). Without this filter the
                // cross-branch conflict detector compares against branches
                // the editor doesn't render → unresolvable "Schedule
                // conflict — can't save" banner. The save path also drops
                // them naturally (atomic-replace deletes anything not in
                // the payload), cleaning up the orphans on first save.
                const enrolledBranchIds = new Set(selectedStaff?.branchIds ?? []);
                const seeded = new Map<SlotKey, ScheduleSlotState>();
                for (const slot of resp.slots ?? []) {
                    if (slot.branchId == null) continue;
                    if (enrolledBranchIds.size > 0 && !enrolledBranchIds.has(slot.branchId)) {
                        // Orphan row for an un-enrolled branch — skip.
                        continue;
                    }
                    const dayNumber = normalizeDayOfWeek(slot.dayOfWeek);
                    seeded.set(slotKey(slot.branchId, dayNumber), {
                        branchId: slot.branchId,
                        dayOfWeek: dayNumber,
                        startTime: normalizeTimeForInput(slot.startTime),
                        endTime: normalizeTimeForInput(slot.endTime),
                        isAvailable: slot.isAvailable,
                    });
                }
                setSchedule(seeded);
                // Smart-default expansion: branch with most days set first;
                // tie-break HQ-primary. If none have hours, expand the first
                // enrolled branch (HQ if present) so the owner can start.
                const counts = new Map<number, number>();
                seeded.forEach(slot => {
                    if (!slot.isAvailable) return;
                    counts.set(slot.branchId, (counts.get(slot.branchId) ?? 0) + 1);
                });
                if (scheduleBranches.length === 0) {
                    setExpandedBranchIds(new Set());
                } else if (scheduleBranches.length === 1) {
                    setExpandedBranchIds(new Set([scheduleBranches[0].id]));
                } else {
                    const sorted = [...scheduleBranches].sort((a, b) => {
                        const da = counts.get(a.id) ?? 0;
                        const db = counts.get(b.id) ?? 0;
                        if (db !== da) return db - da;
                        if (a.isPrimary && !b.isPrimary) return -1;
                        if (!a.isPrimary && b.isPrimary) return 1;
                        return 0;
                    });
                    setExpandedBranchIds(new Set([sorted[0].id]));
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const msg = err instanceof Error ? err.message : 'Could not load schedule.';
                setLastFetchError(msg);
                setSchedule(new Map());
            })
            .finally(() => {
                if (!cancelled) setFetching(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStaffId]);

    // ── Accordion handlers ─────────────────────────────────────────────
    const toggleCardExpanded = (branchId: number) => {
        if (saving) return;
        setExpandedBranchIds(prev => {
            const next = new Set(prev);
            if (next.has(branchId)) next.delete(branchId);
            else next.add(branchId);
            return next;
        });
    };
    const expandAllCards = () => {
        if (saving) return;
        setExpandedBranchIds(new Set(scheduleBranches.map(b => b.id)));
    };
    const collapseAllCards = () => {
        if (saving) return;
        setExpandedBranchIds(new Set());
    };

    // ── Summary helpers ────────────────────────────────────────────────
    const computeBranchSummary = (branchId: number): { daysSet: number; totalHours: number } => {
        let daysSet = 0;
        let totalMinutes = 0;
        schedule.forEach(slot => {
            if (slot.branchId !== branchId || !slot.isAvailable) return;
            daysSet++;
            const [sh, sm] = slot.startTime.split(':').map(Number);
            const [eh, em] = slot.endTime.split(':').map(Number);
            const dur = (eh * 60 + em) - (sh * 60 + sm);
            if (dur > 0) totalMinutes += dur;
        });
        return { daysSet, totalHours: Math.round(totalMinutes / 60) };
    };
    const formatBranchSummary = (s: { daysSet: number; totalHours: number }): string => {
        if (s.daysSet === 0) return 'No hours set';
        const dayLabel = s.daysSet === 1 ? '1 day set' : `${s.daysSet} days set`;
        return `${dayLabel} · ${s.totalHours}h/week`;
    };

    // ── Cell handlers ──────────────────────────────────────────────────
    const setCellAvailable = (branchId: number, dayOfWeek: number, available: boolean) => {
        if (saving) return;
        setSchedule(prev => {
            const next = new Map(prev);
            const key = slotKey(branchId, dayOfWeek);
            const existing = next.get(key);
            if (available) {
                next.set(key, existing
                    ? { ...existing, isAvailable: true }
                    : { branchId, dayOfWeek, startTime: DEFAULT_START, endTime: DEFAULT_END, isAvailable: true });
            } else if (existing) {
                next.set(key, { ...existing, isAvailable: false });
            }
            return next;
        });
    };

    const setCellTime = (
        branchId: number,
        dayOfWeek: number,
        which: 'startTime' | 'endTime',
        value: string,
    ) => {
        if (saving) return;
        setSchedule(prev => {
            const next = new Map(prev);
            const key = slotKey(branchId, dayOfWeek);
            const existing = next.get(key) ?? {
                branchId, dayOfWeek,
                startTime: DEFAULT_START, endTime: DEFAULT_END,
                isAvailable: true,
            };
            next.set(key, { ...existing, [which]: value });
            return next;
        });
    };

    const applyMondayToAllDays = (branchId: number) => {
        if (saving) return;
        setSchedule(prev => {
            const monday = prev.get(slotKey(branchId, 1));
            if (!monday || !monday.isAvailable) return prev;
            const next = new Map(prev);
            for (const day of DAY_INDEXES) {
                if (day === 1) continue;
                const key = slotKey(branchId, day);
                const existing = next.get(key);
                if (!existing || existing.isAvailable) {
                    next.set(key, {
                        branchId, dayOfWeek: day,
                        startTime: monday.startTime, endTime: monday.endTime,
                        isAvailable: true,
                    });
                }
            }
            return next;
        });
    };

    // ── Validation + save ──────────────────────────────────────────────
    const findCrossBranchConflicts = (): string[] => {
        const conflicts: string[] = [];
        type S = { branchId: number; start: string; end: string };
        const byDay = new Map<number, S[]>();
        schedule.forEach(slot => {
            if (!slot.isAvailable) return;
            if (slot.endTime <= slot.startTime) return;
            const arr = byDay.get(slot.dayOfWeek) ?? [];
            arr.push({ branchId: slot.branchId, start: slot.startTime, end: slot.endTime });
            byDay.set(slot.dayOfWeek, arr);
        });
        byDay.forEach((slots, day) => {
            if (slots.length < 2) return;
            for (let i = 0; i < slots.length; i++) {
                for (let j = i + 1; j < slots.length; j++) {
                    const a = slots[i], b = slots[j];
                    if (a.start < b.end && b.start < a.end) {
                        const branchA = branches.find(br => br.id === a.branchId)?.name ?? `Branch ${a.branchId}`;
                        const branchB = branches.find(br => br.id === b.branchId)?.name ?? `Branch ${b.branchId}`;
                        conflicts.push(
                            `${DAYS[day]}: ${branchA} (${a.start}–${a.end}) overlaps ${branchB} (${b.start}–${b.end})`
                        );
                    }
                }
            }
        });
        return conflicts;
    };

    const buildSchedulePayload = (): ScheduleSlotPayload[] => {
        const out: ScheduleSlotPayload[] = [];
        schedule.forEach(slot => {
            out.push({
                branchId: slot.branchId,
                dayOfWeek: slot.dayOfWeek,
                startTime: slot.startTime,
                endTime: slot.endTime,
                isAvailable: slot.isAvailable,
            });
        });
        return out;
    };

    const handleSave = async () => {
        if (saving || selectedStaffId == null) return;
        const next: Record<string, string> = {};
        schedule.forEach(slot => {
            if (!slot.isAvailable) return;
            if (slot.endTime <= slot.startTime) {
                next[`schedule-${slot.branchId}-${slot.dayOfWeek}`] =
                    `${DAYS[slot.dayOfWeek]}: end time must be after start time.`;
            }
        });
        const conflicts = findCrossBranchConflicts();
        if (conflicts.length > 0) {
            next['schedule-cross-branch-conflicts'] = conflicts.join(' · ');
        }
        if (Object.keys(next).length > 0) {
            setErrors(next);
            return;
        }
        setErrors({});
        setSaving(true);
        try {
            await setStaffSchedule(selectedStaffId, buildSchedulePayload());
            onSavedToast?.(`Schedule saved for ${selectedStaff?.fullName ?? 'staff'}.`);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Could not save schedule.';
            onErrorToast?.(msg);
        } finally {
            setSaving(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────
    if (eligibleStaff.length === 0) {
        return (
            <div className={styles.empty}>
                <h3>No active staff yet</h3>
                <p>Add staff in the <strong>Staff list</strong> tab. Once they're active and accepted their invitation, you can manage their working hours here.</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Staff picker — single-select dropdown */}
            <div className={styles.pickerRow}>
                <label htmlFor="schedule-staff-picker" className={styles.pickerLabel}>
                    Manage schedule for
                </label>
                <select
                    id="schedule-staff-picker"
                    className={styles.picker}
                    value={selectedStaffId ?? ''}
                    onChange={e => setSelectedStaffId(parseInt(e.target.value, 10) || null)}
                    disabled={saving || fetching}
                >
                    {eligibleStaff.map(s => (
                        <option key={s.id} value={s.id}>
                            {s.fullName}{s.position ? ` — ${s.position}` : ''}
                        </option>
                    ))}
                </select>
            </div>

            {fetching && (
                <div className={styles.fetchingHint}>Loading schedule…</div>
            )}

            {lastFetchError && !fetching && (
                <div className={styles.errorBanner} role="alert">
                    Couldn't load schedule: {lastFetchError}
                </div>
            )}

            {!fetching && !lastFetchError && selectedStaff && (
                <>
                    {/* Hours apply to readout (multi-branch only — single-branch's card already shows the branch name) */}
                    {hasScheduleBranches && isMultiBranch && (
                        <div className={styles.appliesTo}>
                            <span className={styles.appliesToLabel}>Hours apply to:</span>
                            <div className={styles.appliesToPills}>
                                {scheduleBranches.map(b => (
                                    <span key={b.id} className={styles.appliesToPill}>
                                        <span>{b.name}</span>
                                        {b.isPrimary && (
                                            <span className={styles.appliesToHq} aria-label="Headquarters">HQ</span>
                                        )}
                                    </span>
                                ))}
                            </div>
                            <span className={styles.appliesToHint}>
                                To change which branches this staff works at, edit them in the Staff list tab.
                            </span>
                        </div>
                    )}

                    {/* Cross-branch overlap banner (hard-block on save) */}
                    {errors['schedule-cross-branch-conflicts'] && (
                        <div className={styles.conflictBanner} role="alert">
                            <strong className={styles.conflictBannerTitle}>
                                Schedule conflict — can't save
                            </strong>
                            <p className={styles.conflictBannerCopy}>
                                This staff can't be at two branches at the same time. Resolve the overlaps below to save:
                            </p>
                            <ul className={styles.conflictList}>
                                {errors['schedule-cross-branch-conflicts'].split(' · ').map((conflict, i) => (
                                    <li key={i}>{conflict}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Auto-promote prompt (multi-branch + zero enrollment) */}
                    {isMultiBranch && !hasScheduleBranches && (
                        <div className={styles.autoPromote}>
                            <p className={styles.autoPromoteCopy}>
                                <strong>{selectedStaff.fullName}</strong> isn't enrolled at any specific branch yet, so per-branch hours can't be set. Pick branches in the Staff list tab (Edit staff → Works at branches), then come back here.
                            </p>
                        </div>
                    )}

                    {/* Expand-all / Collapse-all — only renders with ≥3 cards */}
                    {hasScheduleBranches && scheduleBranches.length >= 3 && (
                        <div className={styles.expandAllRow}>
                            <button type="button" className={styles.expandAllLink} onClick={expandAllCards} disabled={saving}>
                                Expand all branches
                            </button>
                            <span className={styles.expandAllSep}>·</span>
                            <button type="button" className={styles.expandAllLink} onClick={collapseAllCards} disabled={saving}>
                                Collapse all
                            </button>
                        </div>
                    )}

                    {/* Branch accordion cards */}
                    {hasScheduleBranches && (
                        <div className={styles.cards}>
                            {scheduleBranches.map(branch => {
                                const isSingleBranch = scheduleBranches.length === 1;
                                const isExpanded = isSingleBranch || expandedBranchIds.has(branch.id);
                                const summary = computeBranchSummary(branch.id);
                                const summaryText = formatBranchSummary(summary);
                                const bodyId = `schedule-tab-card-body-${branch.id}`;
                                const headerId = `schedule-tab-card-header-${branch.id}`;
                                return (
                                    <div key={branch.id} className={styles.card}>
                                        {isSingleBranch ? (
                                            <div className={styles.cardHeaderStatic}>
                                                <span className={styles.cardName}>{branch.name}</span>
                                                {branch.isPrimary && (
                                                    <span className={styles.cardHq} aria-label="Headquarters">HQ</span>
                                                )}
                                                <span className={styles.cardSummary} aria-live="polite">{summaryText}</span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                id={headerId}
                                                className={`${styles.cardHeader} ${isExpanded ? styles.cardHeaderExpanded : ''}`}
                                                onClick={() => toggleCardExpanded(branch.id)}
                                                aria-expanded={isExpanded}
                                                aria-controls={bodyId}
                                                disabled={saving}
                                            >
                                                <span aria-hidden="true" className={`${styles.cardChevron} ${isExpanded ? styles.cardChevronExpanded : ''}`}>▶</span>
                                                <span className={styles.cardName}>{branch.name}</span>
                                                {branch.isPrimary && (
                                                    <span className={styles.cardHq} aria-label="Headquarters">HQ</span>
                                                )}
                                                <span className={styles.cardSummary} aria-live="polite">{summaryText}</span>
                                            </button>
                                        )}
                                        {isExpanded && (
                                            <div
                                                id={bodyId}
                                                role="region"
                                                aria-labelledby={isSingleBranch ? undefined : headerId}
                                                className={styles.cardBody}
                                            >
                                                {DAY_INDEXES.map(day => {
                                                    const slot = schedule.get(slotKey(branch.id, day));
                                                    const enabled = !!slot?.isAvailable;
                                                    const errKey = `schedule-${branch.id}-${day}`;
                                                    return (
                                                        <div key={day} className={styles.dayRow}>
                                                            <span className={styles.dayLabel}>{DAYS[day]}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.toggleBtn}
                                                                data-on={enabled ? 'true' : 'false'}
                                                                onClick={() => setCellAvailable(branch.id, day, !enabled)}
                                                                aria-pressed={enabled}
                                                                aria-label={`${DAYS[day]} at ${branch.name} ${enabled ? 'on' : 'off'}`}
                                                                disabled={saving}
                                                            />
                                                            {enabled ? (
                                                                <span className={styles.dayTimes}>
                                                                    <input
                                                                        type="time"
                                                                        step={900}
                                                                        value={slot?.startTime ?? DEFAULT_START}
                                                                        onChange={e => setCellTime(branch.id, day, 'startTime', e.target.value)}
                                                                        disabled={saving}
                                                                        aria-label={`${DAYS[day]} ${branch.name} start time`}
                                                                        className={styles.timeInput}
                                                                    />
                                                                    <span className={styles.timeSep}>—</span>
                                                                    <input
                                                                        type="time"
                                                                        step={900}
                                                                        value={slot?.endTime ?? DEFAULT_END}
                                                                        onChange={e => setCellTime(branch.id, day, 'endTime', e.target.value)}
                                                                        disabled={saving}
                                                                        aria-label={`${DAYS[day]} ${branch.name} end time`}
                                                                        className={styles.timeInput}
                                                                    />
                                                                </span>
                                                            ) : (
                                                                <span className={styles.offCell}>— Off —</span>
                                                            )}
                                                            {errors[errKey] && (
                                                                <div className={styles.fieldError}>{errors[errKey]}</div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    className={styles.applyAllLink}
                                                    onClick={() => applyMondayToAllDays(branch.id)}
                                                    disabled={saving || !schedule.get(slotKey(branch.id, 1))?.isAvailable}
                                                >
                                                    Apply Monday hours to all working days
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Save row — always shown when a staff is picked */}
                    {hasScheduleBranches && (
                        <div className={styles.saveRow}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save schedule'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ScheduleTab;
